import { getSidebarItem } from "../../../config/sidebarConfig";
import type { DriveItem } from "../../../hooks/drive/driveTypes";
import { noticesQuery } from "../../../services/queries";
import { viewQuery } from "../scopedQuery";
import { getDisplayFileName, getDriveFolderKey } from "../../../utils/driveFolder";
import { DRIVE_PATH, toDriveSearch, toDriveUpload } from "../../../utils/driveNavigation";
import { driveFiles, driveFolderOf } from "../../drive/driveFiles";
import { isInRange } from "../dateRange";
import { askedHow } from "./askedHow";
import { nameMatches } from "../nameMatches";
import { openPageEntry } from "./openPage";
import { askFor, findByName } from "./findByName";
import { AnswerContext, defineEntry, HandoffEntry, HowToEntry } from "./types";
import { capitalize, countOf, during, listOf, quoted, wasWere } from "./wording";

const drivePage = getSidebarItem("drive");
const openDrive = () => ({ label: `Open ${drivePage.label}`, href: DRIVE_PATH });

/** Drive's files, newest first, from the same Notices query Drive uses. */
async function loadFiles({ user, token, queryClient }: AnswerContext) {
  const notices = await queryClient.fetchQuery(viewQuery(user, noticesQuery(user?.schoolId, token)));
  return driveFiles(notices, user?.schoolId);
}


/** How many recent uploads an answer names. */
const LATEST = 5;

/** "Upload a file": Drive's upload, as Home's Drive button opens it. Nothing is uploaded until Upload & Share. */
const uploadEntry: HandoffEntry = {
  id: "drive.upload",
  sidebarItem: "drive",
  examples: [
    "Upload a file",
    "Upload a document",
    "Upload to Drive",
    "Add a file to Drive",
    "Upload a PDF",
    "Upload a photo",
    "Share a file",
    "Put a file in Drive",
    "I want to upload a file",
    "Upload files and share them",
    "Add a document",
    "Upload a worksheet",
    "Upload the permission slip",
  ],
  handoff: async () => ({
    kind: "ready",
    text: "Here's what I understood. Open it to choose your files. Nothing is uploaded until you press Upload & Share.",
    summary: [{ label: "Upload to", value: drivePage.label }],
    to: toDriveUpload(),
  }),
};

/** "How do I add things to Drive?": the steps, with a button that opens the upload dialog straight away. */
const howToUploadEntry: HowToEntry = {
  id: "drive.howToUpload",
  sidebarItem: "drive",
  examples: [
    "How do I add things to Drive?",
    "How do I add files to Drive?",
    "How do I upload to Drive?",
    "How do I upload a worksheet?",
    "How do I upload a file?",
    "How do I put a file in Drive?",
    "How do I share a file with a circle?",
    "How do I add a document to Drive?",
    "How do I get a file into Drive?",
    "What's the process for uploading to Drive?",
    "How do I put a photo in Drive?",
    "how do i add a document",
    "wheres upload",
    "How do I upload a file?",
    "What if the upload fails halfway?",
    "I'll upload it myself then",
    "Who can upload?",
    ...askedHow("upload a file"),
    ...askedHow("upload the indemnity form"),
    "Nothing has been uploaded, correct?",
    "Where do I press Upload & Share?",
    "If I close this chat is the form lost?",
    "Upload & Share is the confirm button",
  ],
  howTo: {
    load: async (_ctx) => ({
      text: "Drive → press UPLOAD FOLDER → add the file, choose the Circle, then press Upload & Share. Nothing is uploaded until you press that.",
      actions: [{ kind: "open", label: "Upload to Drive", to: toDriveUpload() }],
    }),
  },
};

const folderHowToEntry: HowToEntry = {
  id: "drive.folderHowTo",
  sidebarItem: "drive",
  examples: [
    "How do I make a folder in Drive?",
    "Make a folder in Drive for my class",
    "How do I add a new folder in Drive?",
    "How do I upload a folder?",
    "Where is Add new folder?",
    "How do I organise Drive into folders?",
    "How do I make a folder for forms?",
    "How do I create a Drive folder?",
    "What does UPLOAD FOLDER do?",
    "How do I share a folder?",
    "new folder",
    "How do I organise last term's forms?",
    ...askedHow("make a folder for forms"),
  ],
  howTo: {
    load: async () => ({
      text: "On Drive, press Add new folder, or UPLOAD FOLDER to share a named folder with a Circle. Nothing is created until you do that.",
      actions: [{ kind: "open", label: "Open Drive", to: { pathname: DRIVE_PATH, state: undefined } }],
    }),
  },
};

const downloadHowToEntry: HowToEntry = {
  id: "drive.downloadHowTo",
  sidebarItem: "drive",
  examples: [
    "How do I download a file from Drive?",
    "How do I save a PDF from Drive?",
    "How do I download an attachment?",
    "How do I get a file onto my computer?",
    "Where do I download a Drive file?",
    "Can I download from Drive?",
    "How do I open a file to download it?",
    "How do I save a Drive file?",
    "Download a file",
    "How do I get the file out of Drive?",
    "How do I download it?",
    "download an attachment",
    "How do I download it again?",
    "Is there a Download all button?",
    "Download all the forms",
  ],
  howTo: {
    load: async () => ({
      text: "Was it attached to a Notice or shared through Drive? Open the Notice for a Notice attachment, or open Drive and open the file for one uploaded there. There isn't a Download all button.",
      actions: [{ kind: "open", label: "Open Drive", to: { pathname: DRIVE_PATH, state: undefined } }],
    }),
  },
};

const storageHowToEntry: HowToEntry = {
  id: "drive.storageHowTo",
  sidebarItem: "drive",
  examples: [
    "Is there a storage limit?",
    "How much space do we have?",
    "What is the Drive storage limit?",
    "Is there a file size limit?",
    "How much storage does Drive have?",
    "What is the 20 GB limit?",
    "Is storage shared?",
    "How much file storage do we get?",
    "Is there a cap on Drive?",
    "What's our storage allowance?",
    "drive storage",
  ],
  howTo: {
    load: async () => ({
      text: "Each school has a shared file-storage limit of 20 GB. That is the Organization's total allowance, not the maximum size of one upload, which I don't have confirmed. I also don't have how much of the 20 GB is already used.",
      actions: [{ kind: "open", label: "Open Drive", to: { pathname: DRIVE_PATH, state: undefined } }],
    }),
  },
};

const driveVisibilityHowToEntry: HowToEntry = {
  id: "drive.visibilityHowTo",
  sidebarItem: "drive",
  examples: [
    "Who can see a Drive file?",
    "Are Drive files public?",
    "Can parents download Drive files?",
    "Who has access to a Drive folder?",
    "Drive permissions",
    "Who can open a file I upload?",
    "Who sees files I share on Drive?",
    "Are Drive files private?",
    "Who receives a shared Drive folder?",
    "Can everyone see my Drive files?",
    "Who will be able to see it?",
    "Can I share one file with two circles?",
    "Can parents save a copy on their phone?",
    "And they choose a circle at that point?",
    "Who will be able to see it?",
    "Who sees the form?",
  ],
  howTo: {
    load: async (_ctx, asked = "") => ({
      text: /\b(?:download|save a copy|device)\b/i.test(asked)
        ? "When you upload and share, you choose the Circle that should receive it. Parents can open files shared with their Circle in the app. I don't have confirmed steps for saving a copy to their device."
        : "When you upload and share, you choose the Circle that should receive it. Parents can open files shared with their Circle in the app.",
      actions: [{ kind: "open", label: "Open Drive", to: { pathname: DRIVE_PATH, state: undefined } }],
    }),
  },
};

export const driveEntries = [
  openPageEntry("drive", "It's where you access all files and attachments from your Circles.", [
    "Where are our files?",
    "Where are the files I uploaded?",
    "Where are my uploaded files?",
    "Where can I find an attachment?",
    "Where's the PDF in Drive?",
    "Find a file in Drive",
    "where is my stuff",
    "cant find the files",
  ]),

  // The newest files, as Drive lists them
  defineEntry<{ files: string[]; total: number }>({
    id: "drive.recent",
    sidebarItem: "drive",
    examples: [
      "What was uploaded recently?",
      "What are the latest files?",
      "Show me recent uploads",
      "What files were added this week?",
      "Which files are new in Drive?",
      "What's the newest file?",
      "Recent files in Drive",
      "What did we upload last week?",
      "Any new attachments?",
      "How many files were uploaded this month?",
      "Latest documents uploaded",
      "What files do we have in Drive?",
      "What's in Drive?",
      "whats in drive",
      "Tell me what files we have in Drive",
      "What files do we have?",
      "What other files do we have?",
      "What files are in Drive?",
      "What files in Drive?",
    ],
    slots: { dateRange: "allTime" },
    answer: {
      load: async ({ dateRange }, ctx) => {
        const files = (await loadFiles(ctx)).filter((f) => !dateRange || isInRange(f.createdAt, dateRange));
        return { files: files.slice(0, LATEST).map((f) => getDisplayFileName(f.attachment.name)), total: files.length };
      },
      templates: [
        ({ files, total }, { dateRange }) =>
          total === 0
            ? `No files were uploaded to Drive${during(dateRange)}.`
            : `${capitalize(countOf(total, "file"))} ${wasWere(total)} uploaded${during(dateRange)}. The newest: ${listOf(files)}.`,
        ({ files, total }, { dateRange }) =>
          total === 0
            ? `There are no files in Drive${during(dateRange)}.`
            : `Drive has ${countOf(total, "file")}${during(dateRange)}. The newest are ${listOf(files)}.`,
        ({ files, total }, { dateRange }) =>
          total === 0
            ? `Nothing was added to Drive${during(dateRange)}.`
            : `Latest in Drive: ${listOf(files)}, of ${countOf(total, "file")}${during(dateRange)}.`,
      ],
      link: () => openDrive(),
    },
  }),

  uploadEntry,
  howToUploadEntry,
  folderHowToEntry,
  driveVisibilityHowToEntry,
  downloadHowToEntry,
  storageHowToEntry,

  // One file, by name, as Drive's search finds it
  defineEntry<{ name: string; folder?: string; folderKey?: string; query: string }>({
    id: "drive.find",
    sidebarItem: "drive",
    examples: [
      "Find a file",
      "Can you find a file?",
      "Where is the file?",
      "Find me the file",
      "Look for a file in Drive",
      "Search Drive for a file",
      "Can you find me our OTP",
      "Where's our OTP?",
      "Find the OTP",
      "Find a file called Final OTP",
      "Where is the PDF?",
      "Do we have a file called minutes?",
      "Look up a document",
      "Is the invoice in Drive?",
    ],
    slots: { file: "" },
    answer: {
      load: async ({ file: said = "" }, ctx) => {
        const files = await loadFiles(ctx);
        const query = fileQuery(said);
        if (!query) {
          return askFor(
            "file",
            "missing",
            /\bpdfs?\b/i.test(said)
              ? "Which PDF are you looking for? Tell me its name or part of it. Files uploaded to Drive are listed there. A PDF attached to a Notice is on that Notice."
              : "Which file would you like?",
            files.map((file) => getDisplayFileName(file.attachment.name)),
          );
        }
        const found = filesMatching(files, query);
        if (found.length > 1) {
          return askFor(
            "file",
            "ambiguous",
            `Which file called ${quoted(query)} do you mean?`,
            found.map((file) => getDisplayFileName(file.attachment.name)),
          );
        }
        const file = found[0];
        const name = file ? getDisplayFileName(file.attachment.name) : "";
        const folder = file
          ? getDriveFolderKey(file.noticeTitle, file.attachment.name) ?? undefined
          : undefined;
        return {
          name,
          folder,
          folderKey: file ? driveFolderOf(file, ctx.user?.schoolId) : undefined,
          query,
        };
      },
      templates: [
        ({ name, folder, query }) => fileFound(name, folder, query),
        ({ name, folder, query }) =>
          name
            ? `Drive has ${name}${folder ? `, in ${folder}` : ""}.`
            : `There's no file called ${quoted(query)} in Drive.`,
        ({ name, folder, query }) =>
          name
            ? `I found ${name} in Drive${folder ? `, in ${folder}` : ""}.`
            : `I couldn't find ${quoted(query)} in Drive.`,
      ],
      link: (_, { name, folderKey }) => {
        if (!name) return openDrive();
        const { pathname, state } = toDriveSearch(name, folderKey);
        return { label: `Open ${name}`, href: pathname, state };
      },
      subjectOf: ({ name }) => name || undefined,
    },
  }),
];

/** Question words around a file name: "can you find me our OTP" is "OTP". */
const FILE_LEAD =
  /^(?:(?:can|could|would)\s+you\s+)?(?:please\s+)?(?:help\s+me\s+)?(?:find|locate|get|show|search(?:\s+for)?|look(?:\s+up|\s+for)?|where(?:\s+is|\s+are|'s)?|is(?:\s+there)?|do\s+we\s+have)\s+/i;

export function fileQuery(said: string): string {
  let text = said.replace(/[?.!,]+/g, " ").replace(/\s+/g, " ").trim();
  text = text.replace(/\bwheres\b/gi, "where is");
  text = text.replace(FILE_LEAD, "");
  text = text.replace(/^(?:me|us)\s+/i, "");
  text = text.replace(/^(?:our|my|the|a|an)\s+/i, "");
  text = text.replace(/\b(?:files?|documents?|pdfs?|in drive|on drive|from drive|called|named)\b/gi, " ");
  const query = text.replace(/\s+/g, " ").trim();
  return /^(?:where(?:\s+is)?|the|a|an|it)?$/.test(query) ? "" : query;
}

function filesMatching(files: DriveItem[], query: string): DriveItem[] {
  const byName = findByName(files, query, (file) => getDisplayFileName(file.attachment.name));
  const found = byName.length > 0 ? byName : files.filter((file) => nameMatches(getDisplayFileName(file.attachment.name), query));
  const seen = new Set<string>();
  return found.filter((file) => {
    const name = getDisplayFileName(file.attachment.name);
    if (seen.has(name)) return false;
    seen.add(name);
    return true;
  });
}

function fileFound(name: string, folder: string | undefined, query: string): string {
  if (!name) return `I couldn't find a file called ${quoted(query)} in Drive.`;
  return folder ? `${name} is in Drive, in ${folder}.` : `${name} is in Drive.`;
}
